import { ChangeEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Agent } from 'types/agents';

import { useApi } from '../hooks/useApi';

interface IAgentSelectorProps {
  agents: Agent[];
  setAgents: (agents: Agent[]) => void;
}

export const AgentSelector = ({ agents, setAgents }: IAgentSelectorProps) => {
  const { apiFetch } = useApi();

  const [newAgentName, setNewAgentName] = useState('');
  const [apiUrl, setApiUrl] = useState('');
  const [key, setKey] = useState('');
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddAgent = async () => {
    if (!newAgentName || !apiUrl) {
      setIsError(true);
      return;
    }

    setIsLoading(true);
    setIsError(false);

    try {
      const response = await apiFetch('agents', {
        method: 'POST',
        body: JSON.stringify({ name: newAgentName, endpoint: apiUrl, key: key || null }),
      });

      const newAgent = await response.json();
      setAgents([...agents, newAgent]);
      setNewAgentName('');
      setApiUrl('');
      setKey('');

      toast.success('New agent successfully added!', {
        position: 'top-right',
        autoClose: 5000,
      });
    } catch (e) {
      toast.error('Failed to add agent. Please try again later.', {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setApiUrl(e.target.value);
  };

  return (
    <Stack gap={2.5}>
      <Stack gap={1}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
          Agent Name
        </Typography>
        <TextField
          fullWidth
          placeholder="e.g., Support Agent"
          value={newAgentName}
          onChange={(e) => setNewAgentName(e.target.value)}
          variant="outlined"
          error={isError && !newAgentName}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              '&:hover fieldset': {
                borderColor: '#0099CC',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#0099CC',
              },
            },
          }}
        />
      </Stack>

      <Stack gap={1}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
          CXCortex API Endpoint
        </Typography>
        <TextField
          fullWidth
          placeholder="https://agents.cxcortex.ai/api/v1/..."
          onChange={handleChange}
          value={apiUrl}
          variant="outlined"
          error={isError && !apiUrl}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              '&:hover fieldset': {
                borderColor: '#0099CC',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#0099CC',
              },
            },
          }}
        />
      </Stack>

      <Stack gap={1}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
          API Key (optional)
        </Typography>
        <TextField
          fullWidth
          placeholder="Enter API key"
          onChange={(e) => setKey(e.target.value)}
          value={key}
          variant="outlined"
          type="password"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              '&:hover fieldset': {
                borderColor: '#0099CC',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#0099CC',
              },
            },
          }}
        />
      </Stack>

      <Button
        variant="contained"
        size="large"
        onClick={handleAddAgent}
        disabled={isLoading}
        sx={{
          mt: 1,
          backgroundColor: '#0099CC',
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': {
            backgroundColor: '#007AA3',
          },
        }}
      >
        {isLoading ? 'Adding...' : 'Add Agent'}
      </Button>
    </Stack>
  );
};
